/**
 * The Users page: account administration, for administrators only.
 *
 * Lists accounts, creates them, deactivates them and grants or withdraws
 * module access, administrator and developer rights. Visible only to accounts with
 * `is_admin`; the guard that matters is the backend's, this page merely does
 * not offer what it must not.
 *
 * Accounts are never deleted, only deactivated, so that the explorations,
 * selections and reproductions they created keep their owner.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import axios from "axios";
import ExplorationPageShell from "../components/ExplorationPageShell";
import ProfilePhoneComboField from "../components/ProfilePhoneComboField";
import {
  formatPhoneForDb,
  getPhoneCountryOptions,
  parsePhoneFromDb,
} from "../utils/phoneProfile";
import { flattenDjangoRestError } from "../utils/userFacingMessages";
import { MODULE_COLOR_EXPLORER, MODULE_LABELS, MODULE_ORDER } from "../helper/moduleIds";
import { formatDateTime } from "../helper/formatDate";

const USERS_URL = "api/admin/users/";
const userUrl = (id) => `${USERS_URL}${encodeURIComponent(id)}/`;

/** Same minimum as Django's MinimumLengthValidator (the backend decides). */
const MIN_PASSWORD_LENGTH = 8;

const moduleRank = (id) => {
  const index = MODULE_ORDER.indexOf(id);
  return index === -1 ? MODULE_ORDER.length : index;
};

/** Active modules in the home page's order, labelled like its cards. */
export const orderModules = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row.active && !row.is_deleted)
    .map((row) => ({ id: row.id, label: MODULE_LABELS[row.id] || row.module_name }))
    .sort((a, b) => moduleRank(a.id) - moduleRank(b.id));

const fullName = (user) => [user.first_name, user.last_name].filter(Boolean).join(" ");

const emptyForm = () => ({
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  company_name: "",
  role: "",
  phoneCountry: "DE",
  phoneNational: "",
  is_admin: false,
  is_developer: false,
  module_ids: [MODULE_COLOR_EXPLORER],
});

const formFromUser = (user) => {
  const phone = parsePhoneFromDb(user.phone_number || "", "DE");
  return {
    ...emptyForm(),
    username: user.username,
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    company_name: user.company_name || "",
    role: user.role || "",
    phoneCountry: phone.country,
    phoneNational: phone.national,
  };
};

/**
 * Create an account (``target === "new"``) or edit one's profile fields and
 * password. Modules, administrator and developer rights of existing accounts
 * are changed in the table.
 */
function UserDialog({ target, modules, onClose, onSaved }) {
  const creating = target === "new";
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const countryOptions = useMemo(() => getPhoneCountryOptions(), []);

  useEffect(() => {
    if (!target) return;
    setForm(target === "new" ? emptyForm() : formFromUser(target));
    setError(null);
  }, [target]);

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const toggleModule = (id) =>
    setForm((prev) => ({
      ...prev,
      module_ids: prev.module_ids.includes(id)
        ? prev.module_ids.filter((moduleId) => moduleId !== id)
        : [...prev.module_ids, id],
    }));

  const phoneNumber = formatPhoneForDb(form.phoneCountry, form.phoneNational);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username.trim());
  const passwordValid = creating
    ? form.password.length >= MIN_PASSWORD_LENGTH
    : form.password === "" || form.password.length >= MIN_PASSWORD_LENGTH;
  const valid = Boolean(phoneNumber) && passwordValid && (!creating || emailValid);

  const handleSave = async () => {
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company_name: form.company_name.trim(),
      role: form.role.trim(),
      phone_number: phoneNumber,
    };
    if (form.password) payload.password = form.password;
    if (creating) {
      payload.username = form.username.trim();
      payload.is_admin = form.is_admin;
      payload.is_developer = form.is_developer;
      payload.module_ids = modules
        .map((module) => module.id)
        .filter((id) => form.module_ids.includes(id));
    }
    setBusy(true);
    setError(null);
    try {
      const response = creating
        ? await axios.post(USERS_URL, payload)
        : await axios.patch(userUrl(target.id), payload);
      onSaved(response.data, creating);
    } catch (err) {
      console.error("Saving the user failed:", err);
      setError(flattenDjangoRestError(err?.response?.data) || "Could not save the user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(target)} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{creating ? "New user" : "Edit user"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            required={creating}
            disabled={!creating}
            label="Email"
            type="email"
            value={form.username}
            onChange={setField("username")}
            fullWidth
          />
          <TextField
            required={creating}
            label={creating ? "Password" : "New password"}
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={setField("password")}
            helperText={
              creating
                ? `At least ${MIN_PASSWORD_LENGTH} characters.`
                : "Empty keeps the current password."
            }
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="First name"
              value={form.first_name}
              onChange={setField("first_name")}
              fullWidth
            />
            <TextField
              label="Last name"
              value={form.last_name}
              onChange={setField("last_name")}
              fullWidth
            />
          </Stack>
          <TextField
            label="Company name"
            value={form.company_name}
            onChange={setField("company_name")}
            fullWidth
          />
          <TextField label="Job title" value={form.role} onChange={setField("role")} fullWidth />
          <ProfilePhoneComboField
            countryCode={form.phoneCountry}
            nationalDigits={form.phoneNational}
            options={countryOptions}
            onCountryChange={(code) => setForm((prev) => ({ ...prev, phoneCountry: code || "DE" }))}
            onNationalDigitsChange={(digits) =>
              setForm((prev) => ({ ...prev, phoneNational: digits }))
            }
          />
          {creating ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Rights
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={2}>
                {modules.map((module) => (
                  <FormControlLabel
                    key={module.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.module_ids.includes(module.id)}
                        onChange={() => toggleModule(module.id)}
                      />
                    }
                    label={module.label}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={form.is_admin}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, is_admin: event.target.checked }))
                      }
                    />
                  }
                  label="Administrator"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={form.is_developer}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, is_developer: event.target.checked }))
                      }
                    />
                  }
                  label="Developer"
                />
              </Stack>
            </Box>
          ) : null}
          {error ? (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || busy}>
          {busy ? "Saving…" : creating ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Administration page (administrators only, see AdminRoute): every account
 * with its module access, administrator and developer rights. Ticking a box
 * saves at once; accounts are deactivated, never deleted.
 */
export default function UserAdmin() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [dialogTarget, setDialogTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const ownId = localStorage.getItem("userId") || "";

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [usersResponse, modulesResponse] = await Promise.all([
        axios.get(USERS_URL),
        axios.get("api/modules/"),
      ]);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setModules(orderModules(modulesResponse.data));
    } catch (error) {
      console.error("Loading users failed:", error);
      setLoadError("Could not load the users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchUser = async (user, changes, message) => {
    setSavingId(user.id);
    try {
      const response = await axios.patch(userUrl(user.id), changes);
      setUsers((prev) => prev.map((row) => (row.id === user.id ? response.data : row)));
      setToast({ message, severity: "success" });
    } catch (error) {
      console.error("Updating the user failed:", error);
      setToast({
        message: flattenDjangoRestError(error?.response?.data) || "Could not save the change.",
        severity: "error",
      });
    } finally {
      setSavingId(null);
    }
  };

  const toggleModule = (user, moduleId) => {
    const granted = user.module_ids || [];
    const module_ids = modules
      .map((module) => module.id)
      .filter((id) => (id === moduleId ? !granted.includes(id) : granted.includes(id)));
    patchUser(user, { module_ids }, "Saved.");
  };

  const handleSaved = (saved, created) => {
    setUsers((prev) =>
      created
        ? [...prev, saved].sort((a, b) => a.username.localeCompare(b.username))
        : prev.map((row) => (row.id === saved.id ? saved : row))
    );
    setDialogTarget(null);
    setToast({ message: created ? "User created." : "Saved.", severity: "success" });
  };

  const handleConfirmDeactivate = async () => {
    const target = deactivateTarget;
    await patchUser(target, { is_active: false }, "Account deactivated.");
    setDeactivateTarget(null);
  };

  const columnCount = 7 + modules.length;

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Users"
        subtitle="Accounts, module access, administrator and developer rights."
        paperSx={{ border: "none", boxShadow: "none", p: 0 }}
        maxWidth="1300px"
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setDialogTarget("new")}
              disabled={loading || Boolean(loadError)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              New user
            </Button>
          </Stack>

          {loadError ? (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              {loadError}
            </Alert>
          ) : null}

          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading users…
              </Typography>
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
              <Table
                size="small"
                sx={{
                  // Fits the page without a horizontal scrollbar: tight cell
                  // padding, wrapping allowed where a value may be long.
                  "& th, & td": { px: 1.25, py: 0.75 },
                  "& th": { lineHeight: 1.25 },
                }}
              >
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50" } }}>
                    <TableCell>User</TableCell>
                    <TableCell>Company</TableCell>
                    {modules.map((module) => (
                      <TableCell key={module.id} align="center">
                        {module.label}
                      </TableCell>
                    ))}
                    <TableCell align="center">Administrator</TableCell>
                    <TableCell align="center">Developer</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Last login</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnCount}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No users.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => {
                      const own = String(user.id) === ownId;
                      const busy = savingId === user.id;
                      const granted = user.module_ids || [];
                      const name = fullName(user);
                      return (
                        <TableRow
                          key={user.id}
                          hover
                          sx={
                            user.is_active
                              ? undefined
                              : { "& td, & td .MuiTypography-root": { color: "text.disabled" } }
                          }
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {name || user.username}
                            </Typography>
                            {name ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {user.username}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{user.company_name || "—"}</TableCell>
                          {modules.map((module) => (
                            <TableCell key={module.id} align="center" padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={granted.includes(module.id)}
                                disabled={busy}
                                onChange={() => toggleModule(user, module.id)}
                                inputProps={{ "aria-label": `${module.label}: ${user.username}` }}
                              />
                            </TableCell>
                          ))}
                          <TableCell align="center" padding="checkbox">
                            <Tooltip title={own ? "Own account" : ""}>
                              <span>
                                <Checkbox
                                  size="small"
                                  checked={Boolean(user.is_admin)}
                                  disabled={busy || own}
                                  onChange={() =>
                                    patchUser(user, { is_admin: !user.is_admin }, "Saved.")
                                  }
                                  inputProps={{ "aria-label": `Administrator: ${user.username}` }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={Boolean(user.is_developer)}
                              disabled={busy}
                              onChange={() =>
                                patchUser(user, { is_developer: !user.is_developer }, "Saved.")
                              }
                              inputProps={{ "aria-label": `Developer: ${user.username}` }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              color={user.is_active ? "success" : "default"}
                              label={user.is_active ? "Active" : "Inactive"}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>
                            {user.last_login ? formatDateTime(user.last_login) : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap", pl: 0 }}>
                            <Tooltip title="Edit">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busy}
                                  onClick={() => setDialogTarget(user)}
                                  aria-label={`Edit ${user.username}`}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {user.is_active ? (
                              <Tooltip title={own ? "Own account" : "Deactivate"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={busy || own}
                                    onClick={() => setDeactivateTarget(user)}
                                    aria-label={`Deactivate ${user.username}`}
                                  >
                                    <BlockOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Activate">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={busy}
                                    onClick={() =>
                                      patchUser(user, { is_active: true }, "Account activated.")
                                    }
                                    aria-label={`Activate ${user.username}`}
                                  >
                                    <RestoreOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </ExplorationPageShell>

      <UserDialog
        target={dialogTarget}
        modules={modules}
        onClose={() => setDialogTarget(null)}
        onSaved={handleSaved}
      />

      <Dialog
        open={Boolean(deactivateTarget)}
        onClose={() => savingId === null && setDeactivateTarget(null)}
      >
        <DialogTitle>Deactivate account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deactivateTarget?.username} can no longer sign in. Open sessions end immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={savingId !== null}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeactivate}
            disabled={savingId !== null}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Stack>
  );
}
